import { Button, Col, Form, FormInstance, Row, Select, Tooltip, Typography } from "antd";
import { QuestionCircleOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { IsOpenType } from "@bindeleddet/core/types";
import { EditEventFormType } from "../CreateOrEditEvent";
import CompanyCard from "./CompanyCard";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const NORWEGIAN_TIMEZONE = "Europe/Oslo";

const { Option } = Select;
const { Text } = Typography;

interface Props {
  form: FormInstance<EditEventFormType>;
  isEditing?: boolean;
}

const getStatusLabel = (status: IsOpenType): string => {
  switch (status) {
    case IsOpenType.NOT_OPENED:
      return "Ikke åpnet";
    case IsOpenType.OPENED:
      return "Åpen";
    case IsOpenType.CLOSED:
      return "Stengt";
    case IsOpenType.DEREGISTERING_CLOSED:
      return "Avmelding Stengt";
    default:
      return "Ukjent";
  }
};

/**
 * Compute what the status WILL be based on current form values.
 * This mirrors the backend logic in Event.is_open property.
 * Uses Norwegian timezone (Europe/Oslo) for all comparisons.
 */
const computeStatus = (
  manuallyClosed: boolean | undefined,
  openTime: string | undefined,
  closeTime: string | undefined
): IsOpenType => {
  // If manually closed, always return CLOSED
  if (manuallyClosed) {
    return IsOpenType.CLOSED;
  }

  // Get current time in Norwegian timezone
  const now = dayjs().tz(NORWEGIAN_TIMEZONE);

  // Parse times - these are entered as Norwegian time
  const openDayjs = openTime ? dayjs(openTime).tz(NORWEGIAN_TIMEZONE, true) : null;
  const closeDayjs = closeTime ? dayjs(closeTime).tz(NORWEGIAN_TIMEZONE, true) : null;

  // If no open time set, event is not open
  if (!openDayjs || !openDayjs.isValid()) {
    return IsOpenType.NOT_OPENED;
  }

  // Before open time -> NOT_OPENED
  if (now.isBefore(openDayjs)) {
    return IsOpenType.NOT_OPENED;
  }

  // After open time but before close time (or no close time) -> OPEN
  if (!closeDayjs || !closeDayjs.isValid() || now.isBefore(closeDayjs)) {
    return IsOpenType.OPENED;
  }

  // After close time -> DEREGISTRATION_CLOSED
  return IsOpenType.DEREGISTERING_CLOSED;
};

const CompanyCardStatus: React.FC<Props> = ({ form, isEditing = false }) => {
  const onVisibilityChange = (value: number) => {
    form.setFieldsValue({ is_visible: value });
  };

  const handleManualClose = () => {
    form.setFieldsValue({ manually_closed: true });
  };

  const handleManualOpen = () => {
    form.setFieldsValue({ manually_closed: false });
  };

  // Watch form values for reactivity
  const manuallyClosed = Form.useWatch("manually_closed", form);
  const openTime = Form.useWatch("open", form);
  const closeTime = Form.useWatch("close", form);

  // Compute the status based on current form values
  const computedStatus = computeStatus(manuallyClosed, openTime, closeTime);

  return (
    <CompanyCard title="Status">
      <Row gutter={[30, 30]}>
        <Col md={12} xs={24}>
          <Form.Item<EditEventFormType>
            label={
              <span>
                Status{" "}
                <Tooltip
                  title={
                    <div>
                      <b>Status beregnes automatisk:</b>
                      <ul style={{ paddingLeft: "16px", marginBottom: 0, color: "var(--light)" }}>
                        <li>
                          <b>Ikke åpnet:</b> Før åpningstidspunkt - studenter kan ikke melde seg på
                        </li>
                        <li>
                          <b>Åpen:</b> Mellom åpnings- og stengningstidspunkt - studenter kan melde seg på og av
                        </li>
                        <li>
                          <b>Avmelding stengt:</b> Etter stengningstidspunkt - påmelding tillatt, avmelding gir prikk
                        </li>
                        <li>
                          <b>Stengt:</b> Manuelt stengt - ingen påmelding eller avmelding tillatt
                        </li>
                      </ul>
                      <p style={{ marginTop: "8px", marginBottom: 0, color: "var(--light)" }}>
                        Status styres av feltene <b>Åpningstidspunkt</b> og <b>Stengningstidspunkt</b>. Du kan også
                        stenge/gjenåpne arrangementet manuelt med knappen under.
                      </p>
                    </div>
                  }
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Text
                strong
                style={{
                  padding: "8px 16px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "6px",
                  minWidth: "120px",
                }}
              >
                {getStatusLabel(computedStatus)}
              </Text>

              {isEditing && (
                <>
                  {manuallyClosed ? (
                    <Button icon={<UnlockOutlined />} onClick={handleManualOpen} size="small">
                      Åpne
                    </Button>
                  ) : (
                    <Button icon={<LockOutlined />} onClick={handleManualClose} size="small" danger>
                      Steng
                    </Button>
                  )}
                </>
              )}
            </div>
          </Form.Item>

          {/* Hidden fields for form submission */}
          <Form.Item<EditEventFormType> name="is_open" hidden>
            <input type="hidden" />
          </Form.Item>
          <Form.Item<EditEventFormType> name="manually_closed" hidden>
            <input type="hidden" />
          </Form.Item>
        </Col>

        <Col md={12} xs={24}>
          <Form.Item<EditEventFormType> name="is_visible" label="Synlighet">
            <Select onChange={onVisibilityChange} size="large" style={{ width: "98%" }}>
              <Option value={0}>Ikke synlig</Option>
              <Option value={1}>Synlig på nettside</Option>
              <Option value={2}>Synlig i app</Option>
              <Option value={3}>Synlig overalt</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </CompanyCard>
  );
};

export default CompanyCardStatus;
